import { useState, useEffect } from 'react';
import axios from 'axios';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, DollarSign, User, MapPin, CreditCard, Trash2, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DeleteOrderDialog from '../components/DeleteOrderDialog';
import { exportRowsToExcel, fmtDateTime } from '../utils/excelExport';
import { formatBirthday, formatTime12 } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  useEffect(() => {
    fetchPendingOrders();
    fetchSettings();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPendingOrders();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchPendingOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/pending`);
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch pending orders:', error);
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/system-settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };
  
  const openDeleteDialog = (order) => {
    setOrderToDelete({ id: order.id, order_number: order.order_number });
    setDeleteDialogOpen(true);
  };


  const markAsCredit = async (orderId) => {
    if (!window.confirm('Mark this order as Credit Order? It will bypass payment threshold.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/orders/${orderId}/mark-credit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPendingOrders(); // Refresh list
      alert('Order marked as credit successfully!');
    } catch (error) {
      console.error('Failed to mark as credit:', error);
      alert('Failed to mark order as credit');
    }
  };

  const calculatePaymentPercentage = (paid, total) => {
    if (total === 0) return 0;
    return ((paid / total) * 100).toFixed(1);
  };

  const getPaymentStatusColor = (percentage, threshold) => {
    if (percentage >= threshold) return 'bg-green-100 text-green-800 border-green-300';
    if (percentage >= threshold * 0.8) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  if (loading) {
    return (
      <LayoutWithSidebar>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading pending orders...</div>
        </div>
      </LayoutWithSidebar>
    );
  }

  const threshold = settings?.minimum_payment_percentage || 20;

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#e92587' }}>Pending Orders</h1>
            <p className="text-gray-600 mt-1">
              Orders waiting for {threshold}% payment to move to Manage Orders
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => exportRowsToExcel(
              orders,
              [
                { key: 'order_number', label: 'Order #' },
                { key: 'customer_info', label: 'Customer Name', fmt: (v) => v?.name || '' },
                { key: 'customer_info', label: 'Customer Phone', fmt: (v) => v?.phone || '' },
                { key: 'customer_info', label: 'Birthday', fmt: (v) => formatBirthday(v?.birthday) },
                { key: 'flavour', label: 'Flavour' },
                { key: 'size_pounds', label: 'Size (lbs)' },
                { key: 'occasion', label: 'Occasion' },
                { key: 'name_on_cake', label: 'Name on Cake' },
                { key: 'delivery_date', label: 'Delivery Date' },
                { key: 'delivery_time', label: 'Delivery Time' },
                { key: 'needs_delivery', label: 'Needs Delivery' },
                { key: 'delivery_address', label: 'Delivery Address' },
                { key: 'total_amount', label: 'Total (₹)' },
                { key: 'paid_amount', label: 'Paid (₹)' },
                { key: 'pending_amount', label: 'Pending (₹)' },
                { key: 'order_taken_by_name', label: 'Taken By' },
                { key: 'created_at', label: 'Booked At', fmt: fmtDateTime },
              ],
              'pending_orders',
              'PendingOrders'
            )}
            data-testid="pending-export-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Export Excel
          </Button>
        </div>

        {/* Stats Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pending Orders</p>
                <p className="text-3xl font-bold">{orders.length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Payment Threshold</p>
                <p className="text-3xl font-bold text-blue-600">{threshold}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Auto-refresh</p>
                <p className="text-sm font-medium text-green-600">Every 30 seconds</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {orders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No pending orders</p>
              <p className="text-sm text-gray-500 mt-2">All punch orders have met the payment threshold</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {orders.map((order) => {
              const paymentPercentage = calculatePaymentPercentage(
                order.paid_amount || 0,
                order.total_amount
              );
              const statusColor = getPaymentStatusColor(paymentPercentage, threshold);

              return (
                <Card key={order.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">
                        {order.order_number}
                      </CardTitle>
                      <Badge className={statusColor}>
                        {paymentPercentage}%
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Customer Info */}
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{order.customer_info?.name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {order.customer_info?.phone}
                    </div>

                    {/* Order Taken By */}
                    {order.order_taken_by_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 bg-pink-50 rounded px-2 py-1" data-testid={`pending-taken-by-${order.id}`}>
                        <User className="h-4 w-4 text-pink-500" />
                        <span className="text-xs">Taken by:</span>
                        <span className="text-xs font-semibold text-pink-700">{order.order_taken_by_name}</span>
                      </div>
                    )}

                    {/* Delivery Info */}
                    {order.needs_delivery && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span className="truncate">{order.delivery_address}</span>
                      </div>
                    )}

                    {/* Payment Info */}
                    <div className="pt-3 border-t space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="font-semibold">₹{order.total_amount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Paid:</span>
                        <span className="font-semibold text-green-600">
                          ₹{order.paid_amount || 0}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Pending:</span>
                        <span className="font-semibold text-red-600">
                          ₹{order.pending_amount || order.total_amount}
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Payment Progress</span>
                        <span>{paymentPercentage}% / {threshold}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            parseFloat(paymentPercentage) >= threshold
                              ? 'bg-green-500'
                              : parseFloat(paymentPercentage) >= threshold * 0.8
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Delivery Date */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>
                        {order.delivery_date} at {formatTime12(order.delivery_time)}
                      </span>
                    </div>

                    {/* Status Message */}
                    {parseFloat(paymentPercentage) >= threshold ? (
                      <div className="text-xs text-green-600 font-medium">
                        ✓ Ready to move to Manage Orders on next payment sync
                      </div>
                    ) : (
                      <div className="text-xs text-gray-600">
                        Needs ₹{(order.total_amount * threshold / 100 - (order.paid_amount || 0)).toFixed(2)} more
                      </div>
                    )}
                    
                    {/* Mark as Credit Button - Super Admin Only */}
                    {user?.role === 'super_admin' && (
                      <Button
                        onClick={() => markAsCredit(order.id)}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white mt-3"
                        size="sm"
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        Mark as Credit Order
                      </Button>
                    )}
                    
                    {/* Delete Button - Super Admin and Outlet Admin */}
                    {(user?.role === 'super_admin' || user?.role === 'outlet_admin') && (
                      <Button
                        onClick={() => openDeleteDialog(order)}
                        variant="outline"
                        className="w-full border-red-300 text-red-600 hover:bg-red-50 mt-2"
                        size="sm"
                        data-testid={`delete-order-btn-${order.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Order
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <DeleteOrderDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        order={orderToDelete}
        onDeleted={(msg) => {
          setOrderToDelete(null);
          fetchPendingOrders();
          alert(msg || 'Order deleted successfully!');
        }}
      />
    </LayoutWithSidebar>
  );
};

export default PendingOrders;